import IActionsResolver from 'globx/dist/Store/Interfaces/IActionsResolver';
import {Subject}        from 'rxjs';

import InteractionType     from '../../../Analytics/Interactions/Constants/InteractionType';
import IInteractionPayload from '../../../Analytics/Interactions/Interfaces/IInteractionPayload';
import ErrorType           from '../../../Shared/Constants/ErrorType';
import IPlaybackQuality    from '../../../Solutions/Shared/Interfaces/IPlaybackQuality';

import BufferingType    from './Constants/BufferingType';
import PlaybackStatus   from './Constants/PlaybackStatus';
import TimecodeMode     from './Constants/TimecodeMode';
import IPlaybackActions from './Interfaces/IPlaybackActions';
import IUpdateTime      from './Interfaces/IUpdateTime';
import Playback         from './Playback';

const MIN_SEEK_THRESHOLD_SECONDS = 1;

const createPlaybackActionsResolver = (
    interactions$: Subject<IInteractionPayload>
): IActionsResolver<Playback, IPlaybackActions> => (
    value: Playback,
    actions: IPlaybackActions
): IPlaybackActions => ({
    load(): void {
        if (value.playbackStatus === PlaybackStatus.A_LOADING) {
            value.playbackStatus = PlaybackStatus.B_LOADING;
        } else {
            value.playbackStatus = PlaybackStatus.A_LOADING;
        }

        this.resetPlayback();
        this.resetBuffering();
    },

    unload(): void {
        value.playbackStatus = PlaybackStatus.UNLOADED;

        this.resetPlayback();
        this.resetBuffering();
    },

    resetPlayback(): void {
        value.currentTime = 0;
        value.hasPlayed = false;
        value.fatalErrorType = ErrorType.NONE;
    },

    play(): void {
        if (value.isPaused) interactions$.next({
            type: InteractionType.PLAY,
            data: {
                currentTime: value.currentTime
            }
        });

        if (value.hasEnded) {
            value.playbackStatus = PlaybackStatus.A_LOADING;
        } else {
            value.playbackStatus = PlaybackStatus.PLAYING;
        }

        this.resetBuffering();
    },

    pause(): void {
        const hasEnded = value.media.isVod && value.isAtVideoEdge;

        if (!value.hasErrored && !hasEnded) {
            value.playbackStatus = PlaybackStatus.PAUSED;

            interactions$.next({
                type: InteractionType.PAUSE,
                data: {
                    currentTime: value.currentTime
                }
            });
        } else {
            value.playbackStatus = PlaybackStatus.ENDED;
        }
    },

    buffer(bufferingType: BufferingType): void {
        value.playbackStatus = PlaybackStatus.BUFFERING;
        value.bufferingType = bufferingType;
    },

    resetBuffering(): void {
        if (value.bufferingType !== BufferingType.NONE) value.bufferingType = BufferingType.NONE;
    },

    end(): void {
        value.playbackStatus = PlaybackStatus.ENDED;

        this.resetBuffering();
    },

    error(errorType: ErrorType): void {
        value.playbackStatus = PlaybackStatus.ERRORED;
        value.fatalErrorType = errorType;

        this.resetBuffering();
    },

    updateTime(nextTime: IUpdateTime): void {
        actions.updateCurrentTime(nextTime.currentTime);
        actions.updateMinTime(nextTime.minTime);
        actions.updateMaxTime(nextTime.maxTime);
    },

    updateCurrentTime(nextCurrentTime: number): void {
        if (!value.hasPlayed && nextCurrentTime !== value.currentTime) value.hasPlayed = true;

        if (value.hasEnded && nextCurrentTime < value.duration) {
            actions.pause();
        }

        const isSeeking = Math.abs(nextCurrentTime - value.currentTime) > MIN_SEEK_THRESHOLD_SECONDS;

        if (isSeeking) {
            interactions$.next({
                type: nextCurrentTime > value.currentTime ?
                    InteractionType.SEEK_FORWARD : InteractionType.SEEK_BACKWARD,
                data: {
                    currentTime: value.currentTime,
                    nextCurrentTime
                }
            });
        }

        value.currentTime = nextCurrentTime;
    },

    updateMinTime(nextMinTime: number): void {
        value.minTime = nextMinTime;
    },

    updateMaxTime(nextMaxTime: number): void {
        value.maxTime = nextMaxTime;
    },

    updatePlaybackQuality({resolution, bitrate, estimatedBandwidth}: IPlaybackQuality): void {
        value.resolution = resolution;
        value.bitrate = bitrate;
        value.estimatedBandwidth = estimatedBandwidth;
    },

    toggleTimecodeMode(): void {
        const nextTimecodeMode = value.timecodeMode === TimecodeMode.REMAINING ?
            TimecodeMode.DURATION : TimecodeMode.REMAINING;

        interactions$.next({
            type: InteractionType.TIMECODE_CLICK,
            data: {
                currentTime: value.currentTime,
                timecodeMode: value.timecodeMode,
                nextTimecodeMode
            }
        });

        value.timecodeMode = nextTimecodeMode;
    },

    rewind(): void {
        const nextCurrentTime = Math.max(0, value.currentTime - Playback.REWIND_AMOUNT);

        interactions$.next({
            type: InteractionType.REWIND,
            data: {
                currentTime: value.currentTime,
                nextCurrentTime
            }
        });

        value.currentTime = nextCurrentTime;
    },

    fastForward(): void {
        const nextCurrentTime = Math.min(
            value.maxTime - Playback.SEEK_MARGIN_OF_ERROR,
            value.currentTime + Playback.FAST_FORWARD_AMOUNT
        );

        interactions$.next({
            type: InteractionType.FAST_FORWARD,
            data: {
                currentTime: value.currentTime,
                nextCurrentTime
            }
        });

        value.currentTime = nextCurrentTime;
    },

    seekToLiveEdge(): void {
        const nextCurrentTime = value.maxTime;

        interactions$.next({
            type: InteractionType.LIVE_INDICATOR_CLICK,
            data: {
                currentTime: value.currentTime,
                nextCurrentTime
            }
        });

        value.currentTime = nextCurrentTime;
    },

    deriveMedia(nextMedia): void {
        value.media = nextMedia;
    }
});

export default createPlaybackActionsResolver;